import { fromJS } from 'immutable'

import { UPDATE_REPORT_SELECTION_SUCCESS } from 'containers/Reports/constants'

import {
  GET_REPORT_DATA,
  GET_REPORT_DATA_ERROR,
  GET_REPORT_DATA_SUCCESS,
  INVALIDATE_REPORT_DATA_CACHE,
  INVALIDATE_REPORT_DATA_CACHE_ERROR,
  INVALIDATE_REPORT_DATA_CACHE_SUCCESS,
  GET_REPORT_PROGRESS_SUCCESS,
  CLEAR_REPORT_DATA_ERROR,
  REPORT_DATA_WAITING_FOR_CALCULATION,
} from './constants'
import {
  GET_REPORT_DATA_PREVIEW,
  GET_REPORT_DATA_PREVIEW_SUCCESS,
} from 'containers/ReportSelections/constants'

const initialState = fromJS({})

/**
 * Whether an answer belongs to a read the view has already moved on from.
 *
 * This state is keyed by report alone, but the same report is read for one company after another as
 * the user moves between them. A read used to be one round trip, so an abandoned one answered
 * before anyone could switch; with the actuals-calculation wait it can answer minutes later, and
 * letting it write would put one company's figures under another company's heading with nothing on
 * screen to say so. The company asked for last is the one whose answers count.
 *
 * A read that names no company - a customer-level context - is never stale: there is nothing to
 * tell apart, so the guard stands aside rather than guessing.
 */
const answersAStaleCompany = (state, reportId, companyCode) => {
  const requested = state.get(reportId)?.requestedCompanyCode
  return (
    requested !== undefined &&
    companyCode !== undefined &&
    requested !== companyCode
  )
}

const reportDatasReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_REPORT_DATA:
      return state
        .update(action.reportId, (current) => ({
          data: action.setLoading ? undefined : current?.data,
          error: false,
          loading: action.setLoading,
          progress: null,
          // What the guard above compares every later answer against.
          requestedCompanyCode: action.companyCode,
        }))
        .set('recalculating', !action.setLoading)

    case GET_REPORT_DATA_PREVIEW:
      return state.set('recalculating', !action.setLoading)

    case GET_REPORT_DATA_PREVIEW_SUCCESS:
      return state.set('recalculating', false)

    case GET_REPORT_DATA_ERROR:
    case INVALIDATE_REPORT_DATA_CACHE_ERROR:
      if (
        answersAStaleCompany(state, action.error.reportId, action.companyCode)
      ) {
        return state
      }
      return state
        .set(action.error.reportId, {
          loading: false,
          error: action.error,
          progress: null,
          // Carried through the replacement so a straggler from a company the view has left is
          // still recognised as one. The invalidate error names no company, so it keeps whatever
          // the entry already had rather than disarming the guard.
          requestedCompanyCode:
            action.companyCode ??
            state.get(action.error.reportId)?.requestedCompanyCode,
        })
        .set('recalculating', false)

    case CLEAR_REPORT_DATA_ERROR:
      return state
        .update(action.reportId, (report) => ({
          ...report,
          error: undefined,
        }))
        .set('recalculating', false)

    case GET_REPORT_DATA_SUCCESS:
      if (answersAStaleCompany(state, action.reportId, action.companyCode)) {
        return state
      }
      return state
        .set(action.reportId, {
          data: action.report,
          calculated: action.calculated,
          exchangeRates: action.exchangeRates,
          calculationInProgressFor: action.calculationInProgressFor,
          error: false,
          loading: false,
          progress: null,
          companyCode: action.companyCode,
          // Kept through the replacement: two reads of the same report can be in flight at once,
          // and the slower one still has to be recognised when it answers.
          requestedCompanyCode: action.companyCode,
        })
        .set('recalculating', false)

    // Still loading, and now with a reason to show for it. The request is repeated until the
    // calculation releases the company, so this is a stop on the way to data, not an outcome.
    case REPORT_DATA_WAITING_FOR_CALCULATION:
      if (answersAStaleCompany(state, action.reportId, action.companyCode)) {
        return state
      }
      return state.update(action.reportId, (current) => ({
        ...current,
        loading: true,
        error: false,
        progress: null,
        waitingForCalculation: action.companies,
      }))

    case INVALIDATE_REPORT_DATA_CACHE:
      return state.set(action.reportId, {
        error: false,
        loading: true,
      })

    case INVALIDATE_REPORT_DATA_CACHE_SUCCESS:
      return state.delete(action.reportId)

    case UPDATE_REPORT_SELECTION_SUCCESS:
      if (!action.refetchData) return state
      return state.delete(action.reportSelection.id.toString())

    case GET_REPORT_PROGRESS_SUCCESS:
      const { companyCode, progress, reportId } = action
      if (answersAStaleCompany(state, reportId, companyCode)) return state
      // A progress frame means a read is in flight and the API is narrating the wait itself, so
      // the client's own waiting line has stopped being the live one. The two say the same
      // sentence about the same wait - one while a request is open, one in the gap between two -
      // so from the second attempt onwards, leaving both set prints it twice.
      return state.update(reportId, (current) => ({
        ...current,
        progress,
        waitingForCalculation: undefined,
      }))

    default:
      return state
  }
}

export default reportDatasReducer
